import { HomesState } from '@library/store/homes/homes.state';
import { ModulesState } from '@library/store/modules/modules.state';
import { Enums } from '@library/utils/constants/Enums.constant';
import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { createSelector } from '@ngrx/store';
import { Room } from '../rooms/rooms.interface';
import { getcurrentHomeRooms } from '../rooms/rooms.selector';
import { ElectricityPhase, Module } from './modules.interface';

export function isGatewayType(type: string) {
  return (
    type === Enums.ModuleType.NBG ||
    type === Enums.ModuleType.NLG ||
    type === Enums.ModuleType.BNS ||
    type === Enums.ModuleType.OTH ||
    type === Enums.ModuleType.NAPLUG ||
    type === Enums.ModuleType.BAC ||
    type === Enums.ModuleType.NAC ||
    type === Enums.ModuleType.NTG
  );
}

const modulesState = (state: SettingsState) => state.modules;
const homesState = (state: SettingsState) => state.homes;

const currentHomeId = createSelector(homesState, (state: HomesState) => {
  return state.currentHomeId;
});

export const getModules = createSelector(modulesState, (state: ModulesState) => {
  return state.all;
});

export const getCurrentModuleId = createSelector(modulesState, (state: ModulesState) => {
  return state.currentModuleId;
});

export const getCurrentModule = createSelector(
  getModules,
  getCurrentModuleId,
  (modules: Module[], moduleId: string) => {
    return modules.find((module) => module.id === moduleId);
  },
);

export const getCurrentHomeModules = createSelector(
  currentHomeId,
  getModules,
  (currentHomeId: string, modules: Module[]) => {
    return modules?.filter((mod) => mod.homeId === currentHomeId) ?? [];
  },
);

export const getModulesByType = (moduleTypes: string[]) =>
  createSelector(getModules, (modules: Module[]) => {
    return modules.filter((mod) => moduleTypes.includes(mod.type));
  });

export const getModulesById = (moduleIds: string[] = []) =>
  createSelector(getModules, (modules: Module[]) => {
    return modules.filter((module) => moduleIds.includes(module.id));
  });

export const getModuleById = (moduleId: string) =>
  createSelector(getModules, (modules: Module[]) => {
    return modules.find((module) => moduleId === module.id);
  });

export const isPowerLineEcometer = (m: Module) =>
  m.type === 'NLE' && m.consumption_type === 'electrical' && m.customizable;
export const isPowerLineEnergyMeter = (m: Module) =>
  m.type === 'NLPC' && m.electricity_phase === ElectricityPhase.NONE && m.measured_elec_type === 'consumption';
export const isPowerLineBnxm = (m: Module) =>
  m.type === 'BNXM' && m.measured_elec_type === 'consumption' && m.measures_scope === 'device';
export const isPowerLineThreePhasesMeter = (module: Module) =>
  threePhasesMeters.includes(module.type) && module.measures_scope === 'device';

export const getPowerLines = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  return modules.filter((m) => {
    return isPowerLineEcometer(m) || isPowerLineEnergyMeter(m) || isPowerLineThreePhasesMeter(m) || isPowerLineBnxm(m);
  });
});

export const getPhases = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  const totalThreePhase = modules.find((m) => threePhasesMeters.includes(m.type) && m.measures_scope === 'total');
  if (totalThreePhase) {
    return modules.filter(
      (m) => threePhasesMeters.includes(m.type) && m.id.includes(totalThreePhase.id) && m.id.includes('#'),
    );
  }
  return [];
});

export const getCurrentHomeModulesByRoomId = (roomId: string) =>
  createSelector(getcurrentHomeRooms, getCurrentHomeModules, (rooms: Room[], modules: Module[]) => {
    const room: Room = rooms?.find((room) => room.id === roomId);
    return modules?.filter((module) => room?.modules?.includes(module.id)) ?? [];
  });

export const currentHomeBNMH = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  return modules?.find((module) => module.type === Enums.ModuleType.BNMH);
});

const btGatewayList = [Enums.ModuleType.BNCX, Enums.ModuleType.BNMH, Enums.ModuleType.BNHY];

export const currentHomeBtGateway = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  const btGateway = modules.find((module) => {
    return btGatewayList.includes(module.type);
  });
  return !!btGateway;
});

const excludedModules = ['NLV', 'NLLV', 'NLIV', 'NLJ', 'MGSC'];

export const getLiveAppliances = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  return modules.filter((m) => !!m.appliance_type && !excludedModules.includes(m.type));
});

const graphsExcludedModules = ['BNSO'];

export const getGraphsAppliances = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  return modules.filter((m) => {
    // Exclude NLEVSE modules that have nlevse_nlpd_association - only NLPD data should be shown
    if (m.type === 'NLEVSE' && 'nlevse_nlpd_association' in m) return false;

    return !!m.appliance_type && !excludedModules.includes(m.type) && !graphsExcludedModules.includes(m.type);
  });
});

export const AIR_CONDITIONING_MODULES = [Enums.ModuleType.NAC, Enums.ModuleType.BAC, Enums.ModuleType.BIRE];

export const AIR_CONDITIONING_MODULES_WITHOUT_TEMP_HUMIDITY_DATA = [Enums.ModuleType.BIRE];

export const getCurrentHomeACModules = createSelector(getCurrentHomeModules, (modules: Module[]) => {
  return modules.filter((module) => {
    return AIR_CONDITIONING_MODULES.includes(module.type);
  });
});

export const threePhasesMeters = [Enums.ModuleType.NLY, Enums.ModuleType.BNYM];
